import { useCodeiumFlag } from "commands/definitions/settings/useCodeiumCommand";
import { useEnvironmentInterface } from "environment-interface";
import { useEditor } from "features/editor/Feature";
import {
  useAddAlertNotification,
  useDismissNotification,
} from "features/notifications/hooks";
import { useSession } from "hooks/api/useSession";
import {
  hasRepoWriteAccess,
  hasSandboxPermission,
} from "isomorphic/permission";
import { globalCss } from "prism-react";
import { useEffect } from "react";
import type { StatusBarItem } from "vscode";
import { registerExtension } from "vscode/extensions";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useActiveWorkspaceSubscription } from "hooks/useActiveWorkspaceSubscription";
import { useAnalytics } from "hooks/useAnalytics";
import { useScopedPersistedState } from "hooks/usePersistedState";
import { paths } from "utils/router";

import { EnableCodeium } from "./EnableCodeium";
import { useCodeiumModal } from "./useCodeiumModal";

/**
 * For now, we will use localstorage to track
 * the Codeium usage for free sandboxes usage.
 * However, as soon as backend is free, we should
 * implement and store in the database
 */
export const SANDBOX_FREE_USAGE_WEAK_LIMITATION = 100;

const CodeiumIntegrationImple = ({ workspaceId }: { workspaceId: string }) => {
  const editor = useEditor();

  const [codeiumEnabled] = useCodeiumFlag();
  const aiConsentAtWorkspaceLevel = editor.aiConsent;
  const { isFree } = useActiveWorkspaceSubscription();
  const notify = useAddAlertNotification();
  const dismissNotification = useDismissNotification();

  const [codeiumSandboxFreeUsage] = useScopedPersistedState(
    "CODEIUM_SANDBOX_FREE_USAGE",
    0,
    `${workspaceId}/${new Date().getMonth()}`,
  );

  const [neverShowAgain, setNeverShowAgain] = useScopedPersistedState(
    "CODEIUM_UPGRADE_NEVER_SHOW_AGAIN",
    false,
    workspaceId,
  );

  const hasWritePermission =
    editor.type === "branch"
      ? hasRepoWriteAccess(editor.repoPermission)
      : hasSandboxPermission(editor.authorization, "write_code");

  useEffect(() => {
    if (
      !neverShowAgain &&
      isFree &&
      codeiumSandboxFreeUsage >= SANDBOX_FREE_USAGE_WEAK_LIMITATION
    ) {
      notify({
        uniqueName: "codeium",
        message: (
          <>
            Codeium {"{…}:"} you've reached the free limit.
            <br /> Upgrade to Pro for unlimited code-completion suggestions.
          </>
        ),
        actions: [
          {
            onAction: () => {
              window.open(paths.dashboard.upgrade(workspaceId), "_blank");
            },
            text: "Upgrade",
          },
          {
            text: "Never show this again",
            onAction: () => {
              dismissNotification("codeium");
              setNeverShowAgain(true);
            },
          },
        ],
      });
    }
  }, [isFree, codeiumSandboxFreeUsage, neverShowAgain]);

  if (isFree && codeiumSandboxFreeUsage >= SANDBOX_FREE_USAGE_WEAK_LIMITATION) {
    return <UpsellCodeium isFree />;
  }

  const isPublicWorkspace = !editor.workspace;

  if (aiConsentAtWorkspaceLevel && !codeiumEnabled && !isPublicWorkspace) {
    return <UpsellCodeium />;
  }

  if (aiConsentAtWorkspaceLevel && codeiumEnabled && hasWritePermission) {
    return <EnableCodeium workspaceId={workspaceId} />;
  }

  return null;
};

const UpsellCodeium: React.FC<{ isFree?: boolean }> = ({ isFree }) => {
  const { open } = useCodeiumModal();
  const { monacoEditor } = useEnvironmentInterface();
  const track = useAnalytics();

  useEffect(() => {
    if (isFree) {
      track("codeium_reach_free_sandbox_limit");
    }
  }, [isFree]);

  useEffect(() => {
    if (monacoEditor.libraryState.state === "LOADED") {
      let statusBarItem: undefined | StatusBarItem;
      const disposeCommand =
        monacoEditor.libraryState.monaco.editor.registerCommand(
          "codeium.open",
          open,
        );

      const { getApi } = registerExtension(
        {
          name: "Codeium Extension for CodeSandbox",
          publisher: "codesandbox",
          version: "1.0.0",
          engines: { vscode: "*" },
          contributes: {},
        },
        1,
      );

      getApi().then((vscode) => {
        statusBarItem = vscode.window.createStatusBarItem(
          vscode.StatusBarAlignment.Right,
          Infinity, // this is really important
        );

        let text = "Enable Codeium {…}";
        if (isFree) {
          text += ": upgrade to Pro";
        }

        const selector = `.statusbar-item-label[aria-label='${text}']`;

        statusBarItem.command = "codeium.open";
        statusBarItem.text = text;
        statusBarItem.show();

        globalCss({
          [selector]: {
            color: "$accent-primary-base",
          },
        })();
      });

      return () => {
        statusBarItem?.dispose();
        disposeCommand.dispose();
      };
    }
  }, [monacoEditor.libraryState]);

  return null;
};

export const CodeiumIntegration = () => {
  const [session] = useSession();
  const authenticated = session.state === "AUTHENTICATED";
  const workspace = useActiveWorkspace();
  const workspaceId = workspace?.id;

  if (!workspaceId || !authenticated) {
    return null;
  }

  return <CodeiumIntegrationImple workspaceId={workspaceId} />;
};
